import React, { useEffect, useState, useCallback } from "react";
import {
    Table,
    Form,
    Container,
    Row,
    Col,
    InputGroup,
    Button,
    Spinner,
} from "react-bootstrap";
import { getAuthToken } from "../../utils/auth";
import PaginationBar from "../common/PaginationBar";
import { ITEMS_PER_PAGE } from "../../constants/appConstants";

const SubscriptionsManagement = () => {
    const baseUrl = process.env.REACT_APP_API_URL;
    const token = getAuthToken();

    const [subscriptions, setSubscriptions] = useState([]);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(0); // backend is 0-indexed
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);

    const usersPerPage = ITEMS_PER_PAGE;

    const fetchSubscriptions = useCallback(async () => {
        try {
            setLoading(true);

            const url = new URL(`${baseUrl}/admin/subscriptions`);
            url.searchParams.append("page", currentPage);
            url.searchParams.append("size", usersPerPage);
            if (search.trim()) url.searchParams.append("query", search.trim());

            const res = await fetch(url, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            if (!res.ok) {
                console.error("Failed to fetch users:", res.statusText);
                return;
            }

            const result = await res.json();
            if (result.status) {
                setSubscriptions(result.data || []);
                setTotalPages(result.pagination?.totalPages || 0);
            } else {
                setSubscriptions([]);
            }
        } catch (err) {
            console.error("Error fetching users:", err);
        } finally {
            setLoading(false);
        }
    }, [baseUrl, token, usersPerPage, currentPage, search]); // include both currentPage and search

    useEffect(() => {
        fetchSubscriptions();
    }, [fetchSubscriptions]);

    useEffect(() => {
        setCurrentPage(0);
    }, [search]);

    const handleSearch = () => {
        setCurrentPage(0); // reset to first page when searching
    };

    const handleClear = () => {
        setSearch("");
        setCurrentPage(0);
    };

    return (
        <Container className="mt-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h3>Subscriptions</h3>
                </Col>

                <Col xs="5">
                    <InputGroup>
                        <Form.Control
                            type="text"
                            placeholder="Search term..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                        />
                        <Button variant="primary" onClick={handleSearch}>
                            Search
                        </Button>
                        <Button variant="outline-secondary" onClick={handleClear}>
                            Clear
                        </Button>
                    </InputGroup>
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" />
                </div>
            ) : (
                <>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>User</th>
                                <th>Plan</th>
                                <th>Start date</th>
                                <th>End date</th>
                                <th>Status</th>
                                <th>Payment Ref</th>
                            </tr>
                        </thead>

                        <tbody>
                            {subscriptions.length > 0 ? (
                                subscriptions.map((s) => (
                                    <tr>
                                        <td>{s.id}</td>
                                        <td>{s.user}</td>
                                        <td>{s.type}</td>
                                        <td>{s.startDate}</td>
                                        <td>{s.endDate}</td>
                                        <td>{s.status}</td>
                                        <td>{s.paymentReference}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center text-muted">
                                        No users found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    <PaginationBar
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />
                </>
            )}
        </Container>
    );
};

export default SubscriptionsManagement;
